/**
 * enginePreProcessJson_Merge_Slotholder.ts - Slotholder processing for EnginePreProcessJson
 * Pure ES module - works in both Node.js and browser via bundler
 */

import { JsonObject } from '../../../../Assembler/dist/app/json/jsonValue.js';
import { mergeDataJsonIntoHtmlJson } from '../../base/engine/htmlJsonMergeUtil.js';
import {
    getValue,
    getKeys
} from './enginePreProcessJson_Helper.js';
import { TemplateOperation } from '../../../../Assembler/dist/model/templateOperation.js';
import { TemplateOperationKind } from '../../../../Assembler/dist/model/templateOperationKind.js';
import type { TemplateInfoNode } from './enginePreProcessJson_Helper.js';
import {
    TYPE_SLOTHOLDER_SKIP,
    msToNanos,
    getFirstHtmlValue
} from './enginePreProcessJson_Merge_Constants.js';
import type { EnginePreProcessJson, ILoaderPreProcessJson } from './enginePreProcessJson.js';
import type { MergeTraversalContext, PreProcessJsonMergeContext } from './enginePreProcessJson_Merge.js';

type PlainJsonObject = { [key: string]: unknown };

/**
 * MergeSlotComponent function signature
 */
type MergeSlotComponentFn = (
    engine: EnginePreProcessJson,
    loader: ILoaderPreProcessJson,
    context: PreProcessJsonMergeContext,
    slotComponentHtmlJson: JsonObject,
    slotholderData: PlainJsonObject,
    currentTemplate: TemplateInfoNode,
    templateKey: string,
    operations: TemplateOperation[],
    genOperations: boolean,
    jsonData: JsonObject,
    componentHtml: JsonObject | null,
    traversal: MergeTraversalContext
) => string;

/**
 * Process slotholder entry in template
 */
export function processSlotholderEntry(
    engine: EnginePreProcessJson,
    loader: ILoaderPreProcessJson,
    context: PreProcessJsonMergeContext,
    params: {
        key: string;
        keyType: string;
        keyNameFromKey: string;
        value: unknown;
        templateInfo: TemplateInfoNode;
        dataJson: JsonObject;
        operations: TemplateOperation[];
        genOperations: boolean;
        componentHtml: JsonObject | null;
        recursionDepth: number;
        flowStack: string[] | null;
    },
    mergeSlotComponentFn: MergeSlotComponentFn
): { result: string; continue: boolean } {
    const {
        keyType,
        keyNameFromKey,
        value,
        templateInfo,
        dataJson,
        operations,
        genOperations,
        componentHtml,
        recursionDepth,
        flowStack
    } = params;
    const appSite = context.appSiteName;
    const metadata = context.metadata;
    const genComponentHtml = context.genComponentHtml;
    const contextJson = context.contextJson;
    const configJson = context.configJson;

    const startSlot = performance.now();
    const slotholderData = value as PlainJsonObject;
    const slotName = (getValue(slotholderData, 'SlotName') as string) || keyNameFromKey;
    const isSkipMarked = keyType === TYPE_SLOTHOLDER_SKIP || getValue(slotholderData, 'IsSkipMarked') === 'true';

    let resultStr = '';

    if (isSkipMarked) {
        const slotNameWithMarker = '!' + slotName;
        const startGetSlotSkip = performance.now();
        let skipGetResult = loader.getTemplateWithView(appSite, templateInfo.templateName, slotNameWithMarker, metadata.appView, flowStack, configJson, (flowStack?.length ?? 0) + 1, context.clientMode);
        let skipHtmlJson = skipGetResult.htmlJson;
        const resolvedSlotName = skipGetResult.resolvedTemplateName;
        const resolvedSlotAppSite = skipGetResult.resolvedAppSite;
        engine._getTemplateNanos += msToNanos(performance.now() - startGetSlotSkip);
        engine._getTemplateCount++;

        // Apply DynamicView handlers explicitly (Area 7: skip guard inside method handles ! prefix)
        const slotKey = `${resolvedSlotAppSite}_${resolvedSlotName || slotName}`;

        const skipHtml = getFirstHtmlValue(skipHtmlJson!);
        resultStr = skipHtml;

        if (operations && genOperations) {
            const opTemplateName = `${templateInfo.templateAppSite}_${templateInfo.templateName}`;
            operations.push(new TemplateOperation({
                templateName: opTemplateName,
                kind: TemplateOperationKind.SlotSkip,
                target: `{{!#${slotName}}}`,
                replacedWith: slotKey,
                operations: []
            }));
        }

        if (genComponentHtml && componentHtml) {
            componentHtml.set(slotNameWithMarker, skipHtml);
        }

        engine._slotProcessNanos += msToNanos(performance.now() - startSlot);
        engine._slotProcessCount++;
        return { result: resultStr, continue: true };
    }

    // OPTIMIZATION: Use O(1) component index instead of O(n) tree traversal
    const component = loader.getComponentFromIndex(appSite, metadata.appView, slotName);

    // Single GetTemplate + DynamicView + flow markers BEFORE the found/not-found split, matching
    // csharp ProcessSlotholderEntry (Slotholder.cs:141-161): the apply/apply.done engine.slotholder
    // markers and the DynamicView handler boundary are emitted ONCE at slotCompDepth =
    // flowStack.Count + 1 for both the found and not-found (NotFound/Skipped) paths.
    const startGetSlotComp = performance.now();
    const slotGetResult = loader.getTemplateWithView(appSite, templateInfo.templateName, slotName, metadata.appView, flowStack, configJson, (flowStack?.length ?? 0) + 1, context.clientMode);
    let slotComponentHtmlJson = slotGetResult.htmlJson;
    const componentDataJson = slotGetResult.dataJson;
    const resolvedTemplateName = slotGetResult.resolvedTemplateName;
    const resolvedSlotAppSite = slotGetResult.resolvedAppSite;
    engine._getTemplateNanos += msToNanos(performance.now() - startGetSlotComp);
    engine._getTemplateCount++;

    // Update flowStack with resolved template name for correct inheritance lookup
    if (resolvedTemplateName && resolvedTemplateName !== slotName && flowStack && flowStack.length > 0) {
        flowStack[flowStack.length - 1] = resolvedTemplateName;
    }

    if (component) {
        const foundKey = `${component.templateAppSite}_${slotName}`;

        if (slotComponentHtmlJson && getKeys(slotComponentHtmlJson).length > 0) {
            const childOperations: TemplateOperation[] = [];
            const slotDataJson = componentDataJson ?? new JsonObject();

            const startSlotMerge = performance.now();
            const mergedSlotComponentHtmlJson = mergeDataJsonIntoHtmlJson(
                slotComponentHtmlJson,
                slotDataJson,
                foundKey,
                slotName,
                childOperations,
                loader.defaultReplaceHtml,
                false,
                contextJson
            );
            engine._jsonMergeNanos += msToNanos(performance.now() - startSlotMerge);
            engine._jsonMergeCount++;

            const mergeJsonData = getKeys(slotDataJson).length > 0 ? slotDataJson : dataJson;

            if (genComponentHtml && componentHtml) {
                componentHtml.set(slotName, '');
            }

            const slotMerged = mergeSlotComponentFn(
                engine,
                loader,
                context,
                mergedSlotComponentHtmlJson,
                slotholderData,
                component,
                foundKey,
                childOperations,
                genOperations,
                mergeJsonData,
                componentHtml,
                { recursionDepth, flowStack, contextJson, slotName: null, configJson }
            );

            resultStr = slotMerged;

            if (genComponentHtml && componentHtml) {
                componentHtml.set(slotName, slotMerged);
            }

            if (operations && genOperations) {
                const parentTemplateName = templateInfo.placeholderName || templateInfo.templateName;
                const opTemplateName = `${templateInfo.templateAppSite}_${parentTemplateName}`;
                operations.push(new TemplateOperation({
                    templateName: opTemplateName,
                    kind: TemplateOperationKind.SlotReplace,
                    target: `{{#${slotName}}}`,
                    replacedWith: foundKey,
                    operations: childOperations
                }));
            }
        }
    } else {
        // Slot component not found in index - reuse the values already retrieved from getTemplate
        // (matches csharp's not-found branch: no second lookup, no second DynamicView).
        const isSlotSkippedOrMissing = resolvedTemplateName === loader.defaultSkipTemplateName || resolvedTemplateName === loader.defaultReplaceTemplateName;
        const slotFoundKey = `${resolvedSlotAppSite}_${resolvedTemplateName}`;
        const slotChildOperations: TemplateOperation[] = [];
        let defaultHtml = '';

        if (!isSlotSkippedOrMissing) {
            const slotComponentJsonData = componentDataJson ?? new JsonObject();
            const startSlotMergeFallback = performance.now();
            const mergedDefaultHtmlJson = mergeDataJsonIntoHtmlJson(slotComponentHtmlJson!, slotComponentJsonData, slotFoundKey, resolvedTemplateName, slotChildOperations, loader.defaultReplaceHtml, false, contextJson);
            engine._jsonMergeNanos += msToNanos(performance.now() - startSlotMergeFallback);
            engine._jsonMergeCount++;
            defaultHtml = getFirstHtmlValue(mergedDefaultHtmlJson);
        } else {
            defaultHtml = getFirstHtmlValue(slotComponentHtmlJson!);
        }

        let opType: TemplateOperationKind = TemplateOperationKind.SlotReplace;
        if (resolvedTemplateName === loader.defaultSkipTemplateName) {
            opType = TemplateOperationKind.SlotSkip;
        } else if (resolvedTemplateName === loader.defaultReplaceTemplateName) {
            opType = TemplateOperationKind.SlotMissing;
        }

        if (operations && genOperations) {
            const opTemplateName = `${templateInfo.templateAppSite}_${templateInfo.templateName}`;
            operations.push(new TemplateOperation({
                templateName: opTemplateName,
                kind: opType,
                target: `{{#${slotName}}}`,
                replacedWith: `${resolvedSlotAppSite}_${resolvedTemplateName}`,
                operations: slotChildOperations
            }));
        }

        if (genComponentHtml && componentHtml) {
            componentHtml.set(slotName, defaultHtml);
        }

        resultStr = defaultHtml;
    }

    engine._slotProcessNanos += msToNanos(performance.now() - startSlot);
    engine._slotProcessCount++;

    return { result: resultStr, continue: false };
}
